import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Plus, Trash2 } from 'lucide-react'
import apiClient from '@/api/client'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { toastHelpers } from '@/lib/toast-helpers'
import { DIVISOR_LABEL, RULE_KIND_LABEL, YEAR_END_LABEL, dayLabel, ruleDescription, todayISO } from '@/lib/hr'
import { cn } from '@/lib/utils'
import type { HRDivisor, HRHoliday, HRLeaveType, HRRule, HRRuleKind, HRSettings, HRYearEndPolicy } from '@/types/hr'
import { Empty, Field, NativeSelect, Segmented, errMsg } from './shared'
import { useHREmployees, useHRSettings } from './PeopleTab'

// Setup (admin): how pay is worked out, who approves, leave types and what
// happens to unused leave, the bonus / fine rules payroll suggests, and the
// holiday calendar.

type Pane = 'pay' | 'leave' | 'rules' | 'holidays'

export function SetupTab() {
  const [pane, setPane] = useState<Pane>('pay')
  return (
    <div className="space-y-4">
      <Segmented
        value={pane}
        onChange={setPane}
        options={[
          { id: 'pay', label: 'Pay & approval' },
          { id: 'leave', label: 'Leave types' },
          { id: 'rules', label: 'Bonus & fine rules' },
          { id: 'holidays', label: 'Holidays' },
        ]}
      />
      {pane === 'pay' && <PaySettings />}
      {pane === 'leave' && <LeaveTypes />}
      {pane === 'rules' && <Rules />}
      {pane === 'holidays' && <Holidays />}
    </div>
  )
}

function Section({ title, hint, children }: { title: string; hint?: string; children: React.ReactNode }) {
  return (
    <div className="space-y-3 rounded-lg border p-4">
      <div>
        <div className="text-sm font-medium">{title}</div>
        {hint && <p className="mt-0.5 text-xs text-muted-foreground">{hint}</p>}
      </div>
      {children}
    </div>
  )
}

function PaySettings() {
  const qc = useQueryClient()
  const settings = useHRSettings()
  const [v, setV] = useState<HRSettings | null>(null)
  const [deps, setDeps] = useState('')
  useEffect(() => {
    if (settings) {
      setV(settings)
      setDeps(settings.departments.join(', '))
    }
  }, [settings])
  const save = useMutation({
    mutationFn: () => apiClient.hrUpdateSettings({ ...v!, departments: deps.split(',').map((d) => d.trim()).filter(Boolean) }),
    onSuccess: () => {
      toastHelpers.success('Saved')
      qc.invalidateQueries({ queryKey: ['hr'] })
    },
    onError: (err) => toastHelpers.error('Couldn’t save', errMsg(err)),
  })
  if (!v) return <div className="text-sm text-muted-foreground">Loading…</div>
  const set = <K extends keyof HRSettings>(k: K, val: HRSettings[K]) => setV({ ...v, [k]: val })
  const perDay = (d: HRDivisor) => (d === '30' ? 1500 : d === 'month_days' ? 1452 : 1731)
  return (
    <div className="space-y-4">
      <Section title="What one absent day costs (monthly salaries)" hint="Each person can be set differently on their Pay tab.">
        <div className="grid gap-2 sm:grid-cols-3">
          {(Object.keys(DIVISOR_LABEL) as HRDivisor[]).map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => set('absence_divisor', d)}
              className={cn('rounded-lg border p-3 text-left text-sm', v.absence_divisor === d ? 'border-primary ring-1 ring-primary' : 'hover:bg-muted/40')}
            >
              <div className="font-medium">Salary {DIVISOR_LABEL[d]}</div>
              <div className="mt-1 text-xs text-muted-foreground">Rs 45,000 → about Rs {perDay(d).toLocaleString('en-PK')} a day{d === '30' ? ' (most common)' : ''}</div>
            </button>
          ))}
        </div>
      </Section>
      <Section title="Daily-wage staff" hint="Whether someone on a daily wage is paid for days they didn’t work.">
        <label className="flex items-center justify-between gap-3 text-sm">
          Paid for paid-leave days
          <Switch checked={v.daily_pays_paid_leave} onCheckedChange={(c) => set('daily_pays_paid_leave', c)} />
        </label>
        <label className="flex items-center justify-between gap-3 text-sm">
          Paid for holidays
          <Switch checked={v.daily_pays_holidays} onCheckedChange={(c) => set('daily_pays_holidays', c)} />
        </label>
      </Section>
      <Section title="Who approves payroll" hint="Whoever prepares payroll sends it for approval. Approving puts it on everyone’s balance.">
        <NativeSelect value={v.payroll_approver} onChange={(e) => set('payroll_approver', e.target.value as HRSettings['payroll_approver'])}>
          <option value="admin">Only the owner (admin)</option>
          <option value="admin_or_accountant">The owner or the accountant (never their own)</option>
        </NativeSelect>
      </Section>
      <Section title="Leave year">
        <NativeSelect value={v.leave_year} onChange={(e) => set('leave_year', e.target.value as HRSettings['leave_year'])}>
          <option value="calendar">January to December</option>
          <option value="anniversary">From each person’s joining date</option>
        </NativeSelect>
      </Section>
      <Section title="Start month" hint="The first month run here. Anything before it goes in the opening balances when you import.">
        <Input type="month" value={v.start_month} onChange={(e) => set('start_month', e.target.value)} className="w-48" />
      </Section>
      <Section title="Departments" hint="Comma separated.">
        <Input value={deps} onChange={(e) => setDeps(e.target.value)} />
      </Section>
      <div className="flex justify-end">
        <Button onClick={() => save.mutate()} disabled={save.isPending}>
          Save
        </Button>
      </div>
    </div>
  )
}

const EMPTY_TYPE: Partial<HRLeaveType> = {
  name: '',
  code: '',
  paid: true,
  yearly_quota: 0,
  prorate_on_join: true,
  year_end_policy: 'lapse',
  carry_over_max: 0,
  carry_expiry_months: null,
  encash_max: 0,
  encash_on_exit: false,
  active: true,
}

function LeaveTypes() {
  const qc = useQueryClient()
  const q = useQuery({ queryKey: ['hr', 'leave-types', 'all'], queryFn: () => apiClient.hrListLeaveTypes(true) })
  const types = (q.data?.data as HRLeaveType[] | undefined) ?? []
  const [editing, setEditing] = useState<Partial<HRLeaveType> | null>(null)
  const save = useMutation({
    mutationFn: async (t: Partial<HRLeaveType>) => {
      if (t.id) await apiClient.hrUpdateLeaveType(t.id, t)
      else await apiClient.hrCreateLeaveType(t)
    },
    onSuccess: () => {
      toastHelpers.success('Saved')
      qc.invalidateQueries({ queryKey: ['hr'] })
      setEditing(null)
    },
    onError: (err) => toastHelpers.error('Couldn’t save', errMsg(err)),
  })
  const e = editing
  const set = <K extends keyof HRLeaveType>(k: K, val: HRLeaveType[K]) => setEditing({ ...e!, [k]: val })
  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <Button size="sm" onClick={() => setEditing({ ...EMPTY_TYPE })}>
          <Plus className="mr-1.5 h-4 w-4" />
          Add leave type
        </Button>
      </div>
      <div className="divide-y rounded-lg border text-sm">
        {types.map((t) => (
          <button key={t.id} type="button" onClick={() => setEditing(t)} className={cn('flex w-full items-center justify-between gap-3 px-4 py-3 text-left hover:bg-muted/30', !t.active && 'opacity-50')}>
            <div>
              <div className="font-medium">
                {t.name} <span className="text-muted-foreground">({t.code})</span>
                {!t.active && ' · switched off'}
              </div>
              <div className="text-xs text-muted-foreground">
                {t.paid ? 'Paid' : 'Unpaid'} · {t.yearly_quota ? `${t.yearly_quota} days a year` : 'no yearly quota'} · {YEAR_END_LABEL[t.year_end_policy]}
                {t.year_end_policy !== 'lapse' && t.carry_over_max ? ` (carry up to ${t.carry_over_max})` : ''}
                {t.encash_on_exit ? ' · paid out on leaving' : ''}
              </div>
            </div>
            <span className="text-xs text-muted-foreground">Edit</span>
          </button>
        ))}
      </div>
      <Dialog open={!!e} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{e?.id ? `Edit ${e.name}` : 'New leave type'}</DialogTitle>
            <DialogDescription>Changing whether a type is paid only affects days marked from now on.</DialogDescription>
          </DialogHeader>
          {e && (
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-3">
                <Field label="Name" className="col-span-2">
                  <Input value={e.name ?? ''} onChange={(ev) => set('name', ev.target.value)} maxLength={40} />
                </Field>
                <Field label="Code">
                  <Input value={e.code ?? ''} onChange={(ev) => set('code', ev.target.value.toUpperCase())} maxLength={4} placeholder="CL" />
                </Field>
                <Field label="Days a year">
                  <Input type="number" min={0} step={0.5} value={e.yearly_quota ?? 0} onChange={(ev) => set('yearly_quota', Number(ev.target.value))} />
                </Field>
                <label className="col-span-2 flex items-end gap-2 pb-2 text-sm">
                  <Switch checked={!!e.paid} onCheckedChange={(c) => set('paid', c)} /> Paid leave
                </label>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <Switch checked={!!e.prorate_on_join} onCheckedChange={(c) => set('prorate_on_join', c)} /> Joiners get a share for the part of the year left
              </label>
              <Field label="At the end of the leave year, unused days…">
                <NativeSelect value={e.year_end_policy} onChange={(ev) => set('year_end_policy', ev.target.value as HRYearEndPolicy)}>
                  {(Object.keys(YEAR_END_LABEL) as HRYearEndPolicy[]).map((p) => (
                    <option key={p} value={p}>
                      {YEAR_END_LABEL[p]}
                    </option>
                  ))}
                </NativeSelect>
              </Field>
              {(e.year_end_policy === 'carry' || e.year_end_policy === 'carry_then_encash') && (
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Carry at most" hint="0 = no limit">
                    <Input type="number" min={0} step={0.5} value={e.carry_over_max ?? 0} onChange={(ev) => set('carry_over_max', Number(ev.target.value))} />
                  </Field>
                  <Field label="Carried days expire after (months)" hint="0 = never">
                    <Input type="number" min={0} max={24} value={e.carry_expiry_months ?? 0} onChange={(ev) => set('carry_expiry_months', Number(ev.target.value))} />
                  </Field>
                </div>
              )}
              {(e.year_end_policy === 'encash' || e.year_end_policy === 'carry_then_encash') && (
                <Field label="Pay out at most (days)" hint="0 = no limit. Paid at one day’s pay each.">
                  <Input type="number" min={0} step={0.5} value={e.encash_max ?? 0} onChange={(ev) => set('encash_max', Number(ev.target.value))} />
                </Field>
              )}
              <label className="flex items-center gap-2 text-sm">
                <Switch checked={!!e.encash_on_exit} onCheckedChange={(c) => set('encash_on_exit', c)} /> Pay out unused days when someone leaves
              </label>
              {e.id && (
                <label className="flex items-center gap-2 text-sm">
                  <Switch checked={!!e.active} onCheckedChange={(c) => set('active', c)} /> In use
                </label>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>
              Cancel
            </Button>
            <Button onClick={() => e && save.mutate(e)} disabled={!e?.name?.trim() || !e?.code?.trim() || save.isPending}>
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

const RULE_PARAMS: Record<HRRuleKind, { key: string; label: string; hint?: string }[]> = {
  lates_to_day: [{ key: 'every', label: 'Lates that make one day’s cut', hint: 'e.g. 3' }],
  late_flat_fine: [
    { key: 'amount', label: 'Fine per late (Rs)' },
    { key: 'grace_minutes', label: 'Grace minutes', hint: 'Lates up to this many minutes are free' },
  ],
  absent_penalty: [
    { key: 'amount', label: 'Extra fine per absent day (Rs)' },
    { key: 'days', label: 'Extra days’ pay cut per absent day', hint: 'On top of the normal day’s cut' },
  ],
  perfect_attendance_bonus: [
    { key: 'amount', label: 'Bonus (Rs)' },
    { key: 'max_lates', label: 'Lates allowed', hint: '0 = none' },
    { key: 'allow_paid_leave', label: 'Paid leave allowed? (1 = yes)' },
  ],
  fixed_allowance: [{ key: 'amount', label: 'Amount a month (Rs)' }],
}

function Rules() {
  const qc = useQueryClient()
  const settings = useHRSettings()
  const { people } = useHREmployees('active')
  const q = useQuery({ queryKey: ['hr', 'rules'], queryFn: () => apiClient.hrListRules() })
  const rules = (q.data?.data as HRRule[] | undefined) ?? []
  const [editing, setEditing] = useState<Partial<HRRule> | null>(null)
  const [scope, setScope] = useState<'all' | 'department' | 'person'>('all')
  const save = useMutation({
    mutationFn: (r: Partial<HRRule>) => {
      const body = {
        kind: r.kind!,
        name: r.name!,
        params: r.params ?? {},
        department: scope === 'department' ? (r.department ?? null) : null,
        employee_id: scope === 'person' ? (r.employee_id ?? null) : null,
        active: r.active ?? true,
      }
      return r.id ? apiClient.hrUpdateRule(r.id, body).then(() => undefined) : apiClient.hrCreateRule(body).then(() => undefined)
    },
    onSuccess: () => {
      toastHelpers.success('Saved', 'Draft payrolls pick it up the next time they’re opened')
      qc.invalidateQueries({ queryKey: ['hr'] })
      setEditing(null)
    },
    onError: (err) => toastHelpers.error('Couldn’t save', errMsg(err)),
  })
  const toggle = useMutation({
    mutationFn: (r: HRRule) => apiClient.hrUpdateRule(r.id, { active: !r.active }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hr'] }),
  })
  const open = (r: Partial<HRRule>) => {
    setEditing(r)
    setScope(r.employee_id ? 'person' : r.department ? 'department' : 'all')
  }
  const e = editing
  const personName = (id: string | null) => people.find((p) => p.id === id)?.full_name ?? 'someone'
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="max-w-xl text-sm text-muted-foreground">
          Rules don’t change anyone’s pay on their own. Each month they suggest lines on the payroll draft, with the sum shown, and someone keeps or removes each.
        </p>
        <Button size="sm" onClick={() => open({ kind: 'lates_to_day', name: '3 lates = 1 day', params: { every: 3 }, active: true })}>
          <Plus className="mr-1.5 h-4 w-4" />
          Add rule
        </Button>
      </div>
      {rules.length === 0 ? (
        <Empty>No rules yet. Common ones: 3 lates = 1 day’s cut; Rs 3,000 for perfect attendance; a fuel allowance for riders.</Empty>
      ) : (
        <div className="divide-y rounded-lg border text-sm">
          {rules.map((r) => (
            <div key={r.id} className={cn('flex items-center justify-between gap-3 px-4 py-3', !r.active && 'opacity-50')}>
              <button type="button" className="min-w-0 text-left" onClick={() => open(r)}>
                <div className="font-medium">{r.name}</div>
                <div className="text-xs text-muted-foreground">
                  {ruleDescription(r)} · {r.employee_id ? personName(r.employee_id) : r.department ? r.department : 'everyone'}
                </div>
              </button>
              <Switch checked={r.active} onCheckedChange={() => toggle.mutate(r)} aria-label="Rule on" />
            </div>
          ))}
        </div>
      )}
      <Dialog open={!!e} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{e?.id ? 'Edit rule' : 'New rule'}</DialogTitle>
            <DialogDescription>{e?.kind ? ruleDescription({ kind: e.kind, params: e.params ?? {} }) : ''}</DialogDescription>
          </DialogHeader>
          {e && (
            <div className="space-y-3">
              <Field label="Rule">
                <NativeSelect value={e.kind} onChange={(ev) => setEditing({ ...e, kind: ev.target.value as HRRuleKind, params: {} })}>
                  {(Object.keys(RULE_KIND_LABEL) as HRRuleKind[]).map((k) => (
                    <option key={k} value={k}>
                      {RULE_KIND_LABEL[k]}
                    </option>
                  ))}
                </NativeSelect>
              </Field>
              <Field label="Name" hint="Shows on the payslip">
                <Input value={e.name ?? ''} onChange={(ev) => setEditing({ ...e, name: ev.target.value })} maxLength={80} />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                {RULE_PARAMS[e.kind as HRRuleKind].map((p) => (
                  <Field key={p.key} label={p.label} hint={p.hint}>
                    <Input
                      type="number"
                      min={0}
                      value={e.params?.[p.key] ?? ''}
                      onChange={(ev) => setEditing({ ...e, params: { ...(e.params ?? {}), [p.key]: Number(ev.target.value) } })}
                    />
                  </Field>
                ))}
              </div>
              <Field label="Applies to">
                <NativeSelect value={scope} onChange={(ev) => setScope(ev.target.value as typeof scope)}>
                  <option value="all">Everyone</option>
                  <option value="department">One department</option>
                  <option value="person">One person</option>
                </NativeSelect>
              </Field>
              {scope === 'department' && (
                <NativeSelect value={e.department ?? ''} onChange={(ev) => setEditing({ ...e, department: ev.target.value })}>
                  <option value="">Choose…</option>
                  {(settings?.departments ?? []).map((d) => (
                    <option key={d} value={d}>
                      {d}
                    </option>
                  ))}
                </NativeSelect>
              )}
              {scope === 'person' && (
                <NativeSelect value={e.employee_id ?? ''} onChange={(ev) => setEditing({ ...e, employee_id: ev.target.value })}>
                  <option value="">Choose…</option>
                  {people.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.full_name}
                    </option>
                  ))}
                </NativeSelect>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>
              Cancel
            </Button>
            <Button
              onClick={() => e && save.mutate(e)}
              disabled={!e?.name?.trim() || save.isPending || (scope === 'department' && !e?.department) || (scope === 'person' && !e?.employee_id)}
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

function Holidays() {
  const qc = useQueryClient()
  const [year, setYear] = useState(Number(todayISO().slice(0, 4)))
  const [date, setDate] = useState('')
  const [name, setName] = useState('')
  const q = useQuery({ queryKey: ['hr', 'holidays', year], queryFn: () => apiClient.hrListHolidays(year) })
  const list = (q.data?.data as HRHoliday[] | undefined) ?? []
  const add = useMutation({
    mutationFn: () => apiClient.hrAddHoliday({ date, name }),
    onSuccess: () => {
      setDate('')
      setName('')
      qc.invalidateQueries({ queryKey: ['hr'] })
    },
    onError: (err) => toastHelpers.error('Couldn’t save', errMsg(err)),
  })
  const del = useMutation({
    mutationFn: (d: string) => apiClient.hrDeleteHoliday(d),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hr'] }),
    onError: (err) => toastHelpers.error('Couldn’t remove', errMsg(err)),
  })
  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">
        Paid days off for everyone (Eid, 14 August…). They fill themselves in on the register; mark someone present if they worked.
      </p>
      <div className="flex flex-wrap items-end gap-2">
        <Field label="Year">
          <NativeSelect value={year} onChange={(e) => setYear(Number(e.target.value))} className="h-9">
            {[year - 1, year, year + 1].map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </NativeSelect>
        </Field>
        <Field label="Date">
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-9 w-44" />
        </Field>
        <Field label="Name">
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Eid ul Fitr" className="h-9 w-56" />
        </Field>
        <Button size="sm" className="h-9" onClick={() => add.mutate()} disabled={!date || !name.trim() || add.isPending}>
          Add holiday
        </Button>
      </div>
      {list.length === 0 ? (
        <Empty>No holidays set for {year}.</Empty>
      ) : (
        <div className="divide-y rounded-lg border text-sm">
          {list.map((h) => (
            <div key={h.date} className="flex items-center justify-between px-4 py-2.5">
              <div>
                <span className="font-medium">{h.name}</span> <span className="text-muted-foreground">· {dayLabel(h.date, true)}</span>
              </div>
              <Button variant="ghost" size="sm" onClick={() => del.mutate(h.date)} aria-label={`Remove ${h.name}`}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
